import { useState, useMemo } from 'react'
import { BarChart3 } from 'lucide-react'
import { useStandings } from '../../hooks/useStandings'
import SportSelector from '../ui/SportSelector'
import LoadingSpinner from '../ui/LoadingSpinner'
import type { Sport, Standing } from '../../types'

export default function StandingsTable() {
  const [selectedSport, setSelectedSport] = useState<Sport>('NFL')
  const { data: standings, isLoading, error } = useStandings(selectedSport)

  // Group standings by conference/division
  const groupedStandings = useMemo(() => {
    if (!standings) return {}
    return standings.reduce((groups, team) => {
      const key = team.conference || 'League'
      if (!groups[key]) groups[key] = {}
      const divKey = team.division || 'Overall'
      if (!groups[key][divKey]) groups[key][divKey] = []
      groups[key][divKey].push(team)
      return groups
    }, {} as Record<string, Record<string, Standing[]>>)
  }, [standings])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Standings</h1>
        <p className="text-sm text-gray-500">
          Current league standings and team records
        </p>
      </div>

      {/* Sport filter */}
      <SportSelector
        selected={selectedSport}
        onChange={(sport) => {
          if (sport !== 'ALL') setSelectedSport(sport)
        }}
        showAll={false}
      />

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <div className="card p-12 text-center">
          <p className="text-gray-500">Unable to load standings. Please try again.</p>
        </div>
      ) : standings && standings.length > 0 ? (
        <div className="space-y-8">
          {Object.entries(groupedStandings).map(([conference, divisions]) => (
            <div key={conference} className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900">{conference}</h2>
              {Object.entries(divisions).map(([division, teams]) => (
                <DivisionTable
                  key={division}
                  division={division}
                  teams={teams}
                  sport={selectedSport}
                />
              ))}
            </div>
          ))}
        </div>
      ) : (
        <EmptyState sport={selectedSport} />
      )}
    </div>
  )
}

interface DivisionTableProps {
  division: string
  teams: Standing[]
  sport: Sport
}

function DivisionTable({ division, teams, sport }: DivisionTableProps) {
  const sortedTeams = [...teams].sort((a, b) => b.winPercentage - a.winPercentage)

  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <h3 className="font-medium text-gray-900">{division}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-xs text-gray-500 border-b border-gray-200">
              <th className="px-4 py-3 text-left font-medium">Team</th>
              <th className="px-4 py-3 text-center font-medium">W</th>
              <th className="px-4 py-3 text-center font-medium">L</th>
              {sport === 'NFL' && <th className="px-4 py-3 text-center font-medium">T</th>}
              <th className="px-4 py-3 text-center font-medium">PCT</th>
              {sport !== 'NFL' && <th className="px-4 py-3 text-center font-medium">GB</th>}
              <th className="px-4 py-3 text-center font-medium hidden sm:table-cell">STRK</th>
              <th className="px-4 py-3 text-center font-medium hidden md:table-cell">L10</th>
            </tr>
          </thead>
          <tbody>
            {sortedTeams.map((team, idx) => (
              <tr
                key={team.teamId}
                className="border-b border-gray-100 last:border-0 hover:bg-gray-50"
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400 w-4">{idx + 1}</span>
                    <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold text-gray-600">
                      {team.abbreviation}
                    </div>
                    <span className="font-medium text-gray-900">{team.teamName}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-center text-sm font-medium text-gray-900">
                  {team.wins}
                </td>
                <td className="px-4 py-3 text-center text-sm text-gray-600">
                  {team.losses}
                </td>
                {sport === 'NFL' && (
                  <td className="px-4 py-3 text-center text-sm text-gray-600">
                    {team.ties ?? 0}
                  </td>
                )}
                <td className="px-4 py-3 text-center text-sm font-medium text-gray-900">
                  {team.winPercentage.toFixed(3)}
                </td>
                {sport !== 'NFL' && (
                  <td className="px-4 py-3 text-center text-sm text-gray-600">
                    {team.gamesBack !== undefined
                      ? team.gamesBack === 0
                        ? '-'
                        : team.gamesBack
                      : '-'}
                  </td>
                )}
                <td className="px-4 py-3 text-center text-sm text-gray-600 hidden sm:table-cell">
                  <span
                    className={
                      team.streak?.startsWith('W')
                        ? 'text-green-600'
                        : team.streak?.startsWith('L')
                        ? 'text-red-600'
                        : ''
                    }
                  >
                    {team.streak || '-'}
                  </span>
                </td>
                <td className="px-4 py-3 text-center text-sm text-gray-600 hidden md:table-cell">
                  {team.lastTen || '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

interface EmptyStateProps {
  sport: Sport
}

function EmptyState({ sport }: EmptyStateProps) {
  return (
    <div className="card p-12 text-center">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <BarChart3 className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">No Standings Available</h3>
      <p className="text-gray-500">
        {sport} standings data is not available at this time.
      </p>
    </div>
  )
}
